import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CellRenderer, CellRendererParams } from 'app/shared/components/smart-grid/core/interfaces/cell-renderer.interface';
import { LiquidationAttributes } from '../../interfaces/liquidation.interface';
import { UnifiedOrderBadgeComponent } from 'app/shared/components/unified-order-badge/unified-order-badge.component';

/**
 * Liquidation Document Cell Renderer
 *
 * Displays the serial number for liquidation documents.
 * Includes UnifiedOrderBadge support for consistency with other tables.
 *
 * @example
 * ```typescript
 * {
 *   field: 'serial',
 *   headerName: 'No. Documento',
 *   cellRenderer: LiquidationDocumentCellRendererComponent
 * }
 * ```
 */
@Component({
  selector: 'app-liquidation-document-cell-renderer',
  standalone: true,
  imports: [CommonModule, UnifiedOrderBadgeComponent],
  template: `
    <div class="flex items-center gap-2">
      <span class="font-bold text-xs">{{ serial }}</span>

      <app-unified-order-badge
        *ngIf="hasSourceTransactions"
        [order]="orderData">
      </app-unified-order-badge>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LiquidationDocumentCellRendererComponent implements CellRenderer<LiquidationAttributes> {
  @Input() params!: CellRendererParams<LiquidationAttributes>;

  get serial(): string {
    // @ts-ignore - serial may exist on extended data
    return this.params?.data?.serial || '-';
  }

  /**
   * Checks if the data has source transactions
   */
  get hasSourceTransactions(): boolean {
    // @ts-ignore - sourceTransactions may exist on extended data
    return !!(this.params.data?.sourceTransactions?.length);
  }

  /**
   * Order data for the unified order badge
   */
  get orderData(): any {
    return {
      serial: this.serial,
      // @ts-ignore - sourceTransactions may exist on extended data
      sourceTransactions: this.params.data?.sourceTransactions,
      // @ts-ignore - operationType may exist on extended data
      operationType: this.params.data?.operationType,
      // @ts-ignore - isUnified may exist on extended data
      isUnified: this.params.data?.isUnified
    };
  }
}
